import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import Timeline from '../../components/base/Timeline';
import { Col, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { memo } from 'react';
import Scrollbar from '../base/Scrollbar';
import { useAppContext } from '../../providers/AppProvider';
import { faClock } from '@fortawesome/free-regular-svg-icons';
import {
  faCogs,
  faExclamationTriangle,
  faPauseCircle,
  faPlayCircle,
  faShoppingCart,
  faCodeBranch,
  faRetweet,
  faRandom,
  faPen,
  faTrash,
  faBomb,
  faExchangeAlt,
  faLock
} from '@fortawesome/free-solid-svg-icons';
import { faChess, faDove, faDungeon } from '@fortawesome/free-solid-svg-icons';
import { FallingLines } from 'react-loader-spinner';
import { isEqual } from 'lodash';

const iconMapping = {
  faClock: faClock,
  faCogs: faCogs,
  faExclamationTriangle: faExclamationTriangle,
  faPauseCircle: faPauseCircle,
  faPlayCircle: faPlayCircle,
  faChess: faChess,
  faDove: faDove,
  faDungeon: faDungeon,
  faShoppingCart: faShoppingCart,
  faCodeBranch: faCodeBranch,
  faRetweet: faRetweet,
  faRandom: faRandom,
  faPen: faPen,
  // deletions and the test-reset wipes — added with the events in stage 3.
  // An icon name the backend sends but this map does not know falls back to a
  // clock, which is how every event silently looked the same before.
  faTrash: faTrash,
  faBomb: faBomb,
  faExchangeAlt: faExchangeAlt,
  faLock: faLock
};

// Build link path from meta
const getItemLink = (meta) => {
  if (!meta) return null;

  const type = String(meta.type || '').toUpperCase();

  // A deleted product has no page left to open. Linking to it would send the
  // reader to a 404 from the very entry that told them it was deleted.
  if (type === 'JOB_DELETED' || type === 'ORDER_DELETED' || type === 'ORDERS_DELETED') {
    return null;
  }

  // Anything about an order → the order. Not just ORDER_CREATED: edits, status
  // changes and stock reservations all name one, and each was a dead entry
  // while only the creation was linked.
  if (type.startsWith('ORDER') && meta.orderId) {
    return `/apps/sales-management/order-details/${meta.orderId}`;
  }

  // Job-related → project details (uses Redis jobId as route param)
  if (meta.jobId) {
    return `/apps/project-management/project-details/${meta.jobId}`;
  }

  return null;
};

const ActivityTimeline = ({ reports }) => {
  const {
    config: { theme },
    getThemeColor
  } = useAppContext();

  if (!reports) {
    return (
      <div style={styles.spinnerContainer}>
        <FallingLines
          color={theme === 'dark' ? 'white' : '#3874ff'}
          width="100"
          visible={true}
          ariaLabel="falling-circles-loading"
        />
      </div>
    );
  }

  return (
    <Timeline variant="vertical">
      {reports.map((item, index) => {
        const link = getItemLink(item.meta);
        const icon = iconMapping[item.separator?.icon] || faClock;

        return (
          <Timeline.Item className="position-relative" key={item.id}>
            <Row className="g-md-3">
              <Col xs={12} md="auto" className="d-flex">
                <Timeline.OppositeContent>
                  <p className="fs-10 fw-semi-bold text-600 text-end">
                    {item.oppositeContent.date}
                    <br className="d-none d-md-block" />{' '}
                    {item.oppositeContent.time}
                  </p>
                </Timeline.OppositeContent>
                <Timeline.Separator className="position-md-relative">
                  <Timeline.Dot className="bg-primary-100">
                    <FontAwesomeIcon
                      icon={icon}
                      className="text-primary-600 fs-10 dark__text-primary-300"
                    />
                  </Timeline.Dot>

                  {index !== reports.length - 1 && (
                    <Timeline.Bar className="h-100 border-dashed border-400" />
                  )}
                </Timeline.Separator>
              </Col>
              <Col>
                <Timeline.Content>
                  <h5 className="fs-9 lh-sm">
                    {link ? (
                      <Link to={link} className="text-decoration-none text-900 hover-primary">
                        {item.content.title}
                      </Link>
                    ) : (
                      item.content.title
                    )}
                  </h5>
                  <p className="fs-9">
                    by{' '}
                    <span className="fw-semi-bold text-primary">
                      {item.content.user}
                    </span>
                  </p>
                  <p
                    className={classNames('fs-9 text-800', {
                      'mb-5': index !== reports.length - 1,
                      'mb-0': index === reports.length - 1
                    })}
                  >
                    {link ? (
                      <Link to={link} className="text-decoration-none text-800">
                        {item.content.details}
                      </Link>
                    ) : (
                      item.content.details
                    )}
                  </p>
                </Timeline.Content>
              </Col>
            </Row>
          </Timeline.Item>
        );
      })}
    </Timeline>
  );
};

const styles = {
  spinnerContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%'
  }
};
export default memo(ActivityTimeline, (prevProps, nextProps) =>
  isEqual(prevProps.reports, nextProps.reports)
);
